import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

interface BibleBook {
  id: number;
  name: MessageDescriptor;
  abbr: string;
}

const bibleBooks: BibleBook[] = [
  { id: 1, name: msg`Genesis`, abbr: "gen" },
  { id: 2, name: msg`Exodus`, abbr: "exo" },
  { id: 3, name: msg`Leviticus`, abbr: "lev" },
  { id: 4, name: msg`Numbers`, abbr: "num" },
  { id: 5, name: msg`Deuteronomy`, abbr: "deu" },
  { id: 6, name: msg`Joshua`, abbr: "jos" },
  { id: 7, name: msg`Judges`, abbr: "jdg" },
  { id: 8, name: msg`Ruth`, abbr: "rth" },
  { id: 9, name: msg`1 Samuel`, abbr: "1sa" },
  { id: 10, name: msg`2 Samuel`, abbr: "2sa" },
  { id: 11, name: msg`1 Kings`, abbr: "1ki" },
  { id: 12, name: msg`2 Kings`, abbr: "2ki" },
  { id: 13, name: msg`1 Chronicles`, abbr: "1ch" },
  { id: 14, name: msg`2 Chronicles`, abbr: "2ch" },
  { id: 15, name: msg`Ezra`, abbr: "ezr" },
  { id: 16, name: msg`Nehemiah`, abbr: "neh" },
  { id: 17, name: msg`Esther`, abbr: "est" },
  { id: 18, name: msg`Job`, abbr: "job" },
  { id: 19, name: msg`Psalms`, abbr: "psa" },
  { id: 20, name: msg`Proverbs`, abbr: "pro" },
  { id: 21, name: msg`Ecclesiastes`, abbr: "ecc" },
  { id: 22, name: msg`Song of Songs`, abbr: "son" },
  { id: 23, name: msg`Isaiah`, abbr: "isa" },
  { id: 24, name: msg`Jeremiah`, abbr: "jer" },
  { id: 25, name: msg`Lamentations`, abbr: "lam" },
  { id: 26, name: msg`Ezekiel`, abbr: "eze" },
  { id: 27, name: msg`Daniel`, abbr: "dan" },
  { id: 28, name: msg`Hosea`, abbr: "hos" },
  { id: 29, name: msg`Joel`, abbr: "joe" },
  { id: 30, name: msg`Amos`, abbr: "amo" },
  { id: 31, name: msg`Obadiah`, abbr: "oba" },
  { id: 32, name: msg`Jonah`, abbr: "jon" },
  { id: 33, name: msg`Micah`, abbr: "mic" },
  { id: 34, name: msg`Nahum`, abbr: "nah" },
  { id: 35, name: msg`Habakkuk`, abbr: "hab" },
  { id: 36, name: msg`Zephaniah`, abbr: "zep" },
  { id: 37, name: msg`Haggai`, abbr: "hag" },
  { id: 38, name: msg`Zechariah`, abbr: "zec" },
  { id: 39, name: msg`Malachi`, abbr: "mal" },
  { id: 40, name: msg`Matthew`, abbr: "mat" },
  { id: 41, name: msg`Mark`, abbr: "mar" },
  { id: 42, name: msg`Luke`, abbr: "luk" },
  { id: 43, name: msg`John`, abbr: "joh" },
  { id: 44, name: msg`Acts`, abbr: "act" },
  { id: 45, name: msg`Romans`, abbr: "rom" },
  { id: 46, name: msg`1 Corinthians`, abbr: "1co" },
  { id: 47, name: msg`2 Corinthians`, abbr: "2co" },
  { id: 48, name: msg`Galatians`, abbr: "gal" },
  { id: 49, name: msg`Ephesians`, abbr: "eph" },
  { id: 50, name: msg`Philippians`, abbr: "phi" },
  { id: 51, name: msg`Colossians`, abbr: "col" },
  { id: 52, name: msg`1 Thessalonians`, abbr: "1th" },
  { id: 53, name: msg`2 Thessalonians`, abbr: "2th" },
  { id: 54, name: msg`1 Timothy`, abbr: "1ti" },
  { id: 55, name: msg`2 Timothy`, abbr: "2ti" },
  { id: 56, name: msg`Titus`, abbr: "tit" },
  { id: 57, name: msg`Philemon`, abbr: "phm" },
  { id: 58, name: msg`Hebrews`, abbr: "heb" },
  { id: 59, name: msg`James`, abbr: "jas" },
  { id: 60, name: msg`1 Peter`, abbr: "1pe" },
  { id: 61, name: msg`2 Peter`, abbr: "2pe" },
  { id: 62, name: msg`1 John`, abbr: "1jn" },
  { id: 63, name: msg`2 John`, abbr: "2jn" },
  { id: 64, name: msg`3 John`, abbr: "3jn" },
  { id: 65, name: msg`Jude`, abbr: "jud" },
  { id: 66, name: msg`Revelation`, abbr: "rev" },
];

const abbrToIdMap = new Map<string, number>();
const idToAbbrMap = new Map<number, string>();
const idToNameMap = new Map<number, MessageDescriptor>();

for (const book of bibleBooks) {
  const lowerAbbr = book.abbr.toLowerCase();
  abbrToIdMap.set(lowerAbbr, book.id);
  idToAbbrMap.set(book.id, lowerAbbr);
  idToNameMap.set(book.id, book.name);
}

export { bibleBooks };

export function abbrToId(abbr: string): number {
  return abbrToIdMap.get(abbr.toLowerCase()) ?? 1;
}

/**
 * `true` if the reader's book+chapter reference is valid: the book exists and
 * the chapter is an integer within the book's canonical range. Prevents an
 * invalid URL (`/bible/kjv/zzz/3` or `/bible/kjv/gen/999`) from leaving the
 * reader broken or loading forever.
 */
export function isValidReference(abbr: string, chapter: string | number): boolean {
  const bookId = abbrToIdMap.get(abbr.toLowerCase());
  if (bookId === undefined) {
    return false;
  }
  const max = bookChapters[bookId];
  if (!max) {
    return false;
  }
  const chapterNum = typeof chapter === "number" ? chapter : Number.parseInt(chapter, 10);
  return Number.isInteger(chapterNum) && chapterNum >= 1 && chapterNum <= max;
}

export function idToAbbr(id: number): string {
  return idToAbbrMap.get(id) ?? "gen";
}

/** Returns the MessageDescriptor for the book name; pass to t() at render time. */
export function getBookNameDescriptor(id: number): MessageDescriptor {
  return idToNameMap.get(id) ?? msg`Genesis`;
}

interface BookGroup {
  /** Stable English key used in URL params and filtering logic — never translated. */
  key: string;
  name: MessageDescriptor;
  ids: number[];
}

/* oxlint-disable no-magic-numbers */
function range(from: number, to: number): number[] {
  return Array.from({ length: to - from + 1 }, (_, i) => from + i);
}

// Canonical groups (parity with the legacy categories: pentateuch, history,
// poetry_books, major/minor_prophets, gospels, pauline/general_epistles, old/new_testament)
export const bookGroups: BookGroup[] = [
  { key: "Pentateuco", name: msg`Pentateuch`, ids: range(1, 5) },
  { key: "Históricos", name: msg`Historical Books`, ids: range(6, 17) },
  { key: "Poéticos", name: msg`Poetic Books`, ids: range(18, 22) },
  { key: "Profetas Mayores", name: msg`Major Prophets`, ids: range(23, 27) },
  { key: "Profetas Menores", name: msg`Minor Prophets`, ids: range(28, 39) },
  { key: "Evangelios", name: msg`Gospels`, ids: range(40, 43) },
  { key: "Epístolas Paulinas", name: msg`Pauline Epistles`, ids: range(45, 57) },
  { key: "Epístolas Generales", name: msg`General Epistles`, ids: range(58, 65) },
  { key: "Antiguo Testamento", name: msg`Old Testament`, ids: range(1, 39) },
  { key: "Nuevo Testamento", name: msg`New Testament`, ids: range(40, 66) },
];
/* oxlint-enable no-magic-numbers */

export const bookChapters: Record<number, number> = {
  1: 50,
  2: 40,
  3: 27,
  4: 36,
  5: 34,
  6: 24,
  7: 21,
  8: 4,
  9: 31,
  10: 24,
  11: 22,
  12: 25,
  13: 29,
  14: 36,
  15: 10,
  16: 13,
  17: 10,
  18: 42,
  19: 150,
  20: 31,
  21: 12,
  22: 8,
  23: 66,
  24: 52,
  25: 5,
  26: 48,
  27: 12,
  28: 14,
  29: 3,
  30: 9,
  31: 1,
  32: 4,
  33: 7,
  34: 3,
  35: 3,
  36: 3,
  37: 2,
  38: 14,
  39: 4,
  40: 28,
  41: 16,
  42: 24,
  43: 21,
  44: 28,
  45: 16,
  46: 16,
  47: 13,
  48: 6,
  49: 6,
  50: 4,
  51: 4,
  52: 5,
  53: 3,
  54: 6,
  55: 4,
  56: 3,
  57: 1,
  58: 13,
  59: 5,
  60: 5,
  61: 3,
  62: 5,
  63: 1,
  64: 1,
  65: 1,
  66: 22,
};

interface ChapterRef {
  bookId: number;
  chapter: number;
}

// Parity with the legacy prev-next-chapter-helper: crosses book boundaries.
export function getPrevChapter(bookId: number, chapter: number): ChapterRef | undefined {
  if (chapter > 1) {
    return { bookId, chapter: chapter - 1 };
  }
  const prevBook = bookId - 1;
  const prevMax = bookChapters[prevBook];
  if (prevMax) {
    return { bookId: prevBook, chapter: prevMax };
  }
  return undefined;
}

export function getNextChapter(bookId: number, chapter: number): ChapterRef | undefined {
  const max = bookChapters[bookId];
  if (max && chapter < max) {
    return { bookId, chapter: chapter + 1 };
  }
  if (bookChapters[bookId + 1]) {
    return { bookId: bookId + 1, chapter: 1 };
  }
  return undefined;
}
